import { useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import {
  PurchaseRequestItemV2,
  PurchaseRequestV2,
  OrderToSubmitRequest,
} from "../../types";
import {
  usePurchaseRequestV2,
  useItemsByIds,
} from "../../hooks/usePurchaseRequestsV2";
import { useOrder } from "../../hooks/useOrders";
import { buildEditOrderEntry, editWindowOf } from "./buildEditOrderEntry";
import { actions } from "./orderByRequestConstants";
import {
  buildPrefillOrders,
  isSameItemSet,
  mapRequestFiles,
} from "./requestPrefill";

type PrefillArgs = {
  user: any;
  ordersToSubmit: OrderToSubmitRequest[];
  setOrdersToSubmit: React.Dispatch<
    React.SetStateAction<OrderToSubmitRequest[]>
  >;
  setGeneralBidData: (action: any) => void;
  setSelectedType: (value: any) => void;
  setSelectedComplexType: (value: any) => void;
  setIsAllSelected: (value: boolean) => void;
  setActiveStep: (value: number) => void;
};

/**
 * The launch-from-PR prefill: reads `itemIds`/`purchaseRequestId`/`orderId` from
 * the query string, fetches the PR items (or the existing order in edit mode) and
 * hydrates `ordersToSubmit` + `generalBidData`. Extracted verbatim from
 * OrderByRequest.tsx so the RFQ prefill and the edit hydration stay byte-identical
 * (T6 rulings: PREFILL PRESERVED, buildEditOrderEntry is SSOT).
 */
export const useOrderByRequestPrefill = ({
  user,
  ordersToSubmit,
  setOrdersToSubmit,
  setGeneralBidData,
  setSelectedType,
  setSelectedComplexType,
  setIsAllSelected,
  setActiveStep,
}: PrefillArgs) => {
  const [searchParams] = useSearchParams();

  const purchaseRequestId = searchParams.get("purchaseRequestId");
  const selectedItemsParam = searchParams.get("selectedItems") || "";
  const itemIdsParam = searchParams.get("itemIds") || "";
  const orderId = searchParams.get("orderId");
  const isEditMode = !!orderId;

  // Support both old and new flow
  const itemIdsToFetch = itemIdsParam
    ? itemIdsParam.split(",").filter(Boolean)
    : [];

  // Track the URL "selection key" so we can detect when the user navigates here
  // with a different selection of items and reset the flow accordingly.
  const selectionKey = `${orderId ?? ""}|${purchaseRequestId ?? ""}|${itemIdsParam}|${selectedItemsParam}`;
  const lastSelectionKeyRef = useRef<string>(selectionKey);

  const { data: fetchedItems, isLoading: isLoadingItems } = useItemsByIds(
    itemIdsToFetch,
    itemIdsToFetch.length > 0
  );

  const derivedPurchaseRequestId =
    purchaseRequestId || fetchedItems?.[0]?.purchaseRequestId || "";

  const { data: purchaseRequest, isLoading: isLoadingPurchaseRequests } =
    usePurchaseRequestV2(derivedPurchaseRequestId, !!derivedPurchaseRequestId);

  const { data: existingOrder } = useOrder(orderId ?? "", isEditMode);

  // Reset the bidding flow whenever the user lands here with a new set of items.
  useEffect(() => {
    if (lastSelectionKeyRef.current === selectionKey) return;
    lastSelectionKeyRef.current = selectionKey;
    setOrdersToSubmit([]);
    setActiveStep(1);
    setSelectedType(null);
    setSelectedComplexType(null);
    setIsAllSelected(false);
  }, [selectionKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // Edit mode: hydrate from an existing order (not from a purchase request).
  useEffect(() => {
    if (!isEditMode) return;
    if (!existingOrder) return;
    if (existingOrder.id === ordersToSubmit[0]?.orderId) return;
    setOrdersToSubmit([buildEditOrderEntry(existingOrder)]);
    setGeneralBidData({
      type: actions.UPDATE_PAYMENT_METHOD,
      paymentMethod: existingOrder.paymentMethod ?? "",
    });
    setGeneralBidData({
      type: actions.UPDATE_PAYMENT_TERM,
      paymentTerm: existingOrder.paymentTerm ?? "",
    });
    setGeneralBidData({
      type: actions.UPDATE_BUYER_PICKS_UP,
      buyerPicksUp: Boolean(existingOrder.buyerPicksUp),
    });
    const window = editWindowOf(existingOrder);
    setGeneralBidData({
      type: actions.UPDATE_START_DATE,
      startDateValue: window.startDateValue,
    });
    setGeneralBidData({
      type: actions.UPDATE_FINISH_DATE,
      endDateValue: window.endDateValue,
    });
    setGeneralBidData({
      type: actions.UPDATE_START_TIME,
      startTimeValue: window.startTimeValue,
    });
    setGeneralBidData({
      type: actions.UPDATE_FINISH_TIME,
      endTimeValue: window.endTimeValue,
    });
    const consultationStartDate = (existingOrder as any).consultationStartDate;
    const consultationEndDate = (existingOrder as any).consultationEndDate;
    if (consultationStartDate) {
      setGeneralBidData({
        type: actions.UPDATE_CONSULTATION_START,
        consultationStartValue: consultationStartDate,
      });
    }
    if (consultationEndDate) {
      setGeneralBidData({
        type: actions.UPDATE_CONSULTATION_END,
        consultationEndValue: consultationEndDate,
      });
    }
    if (existingOrder.internalCode) {
      setGeneralBidData({
        type: actions.UPDATE_INTERNAL_CODE,
        internalCode: existingOrder.internalCode,
      });
    }
    if (existingOrder.name) {
      setGeneralBidData({
        type: actions.UPDATE_NAME,
        name: existingOrder.name,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditMode, existingOrder?.id]);

  // Create mode: hydrate from the selected purchase-request items.
  useEffect(() => {
    if (isEditMode) return;
    if (!user) return;

    let relevantItems: PurchaseRequestItemV2[] = [];
    let requestData: PurchaseRequestV2 | null = null;

    if (itemIdsToFetch.length > 0 && fetchedItems) {
      if (!purchaseRequest) return;
      relevantItems = fetchedItems.filter(
        (item) => !item.deleted && itemIdsToFetch.includes(item.id)
      );
      requestData = purchaseRequest;
    } else if (purchaseRequestId && purchaseRequest) {
      requestData = purchaseRequest;
      relevantItems = purchaseRequest.items.filter((item) => !item.deleted);
      if (selectedItemsParam) {
        const selectedIds = new Set(selectedItemsParam.split(","));
        relevantItems = relevantItems.filter((item) =>
          selectedIds.has(item.id)
        );
      }
    }

    if (!relevantItems.length) return;

    setOrdersToSubmit((prev) => {
      // Preserve user edits on intermediate re-renders when the set is unchanged.
      if (isSameItemSet(prev, relevantItems)) return prev;
      return buildPrefillOrders(relevantItems, requestData);
    });

    if (requestData) {
      setGeneralBidData({ type: actions.UPDATE_NAME, name: requestData.name });
      setGeneralBidData({
        type: actions.UPDATE_INTERNAL_CODE,
        internalCode: requestData?.purchaseRequestCode ?? undefined,
      });
      setGeneralBidData({
        type: actions.UPDATE_FILES,
        files: mapRequestFiles(requestData),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [purchaseRequest, fetchedItems, user, itemIdsToFetch.length]);

  return {
    isEditMode,
    isLoadingItems,
    isLoadingPurchaseRequests,
  };
};
